/**
 * 游戏 1：小精灵救援行动
 * ------------------------------------------------------------
 * 玩法：森林里的小精灵被魔法水晶困住。每答对一题 → 水晶裂开、能量注入 → 救出 1 只小精灵。
 * 数据：题目由调用方用练习生成器生成（单词/句子/语法/混合都支持），这里不写死任何内容。
 * 判分：复用 engines/graders，保证与普通练习口径一致。
 */
import { registerGame, GameBase } from '../registry.js';
import { el, shuffle, speakEn, escapeHtml } from '../../utils/index.js';

const ELVES = ['🧚', '🧝', '🧞', '🧙', '👼', '🦄', '🐉', '🦋'];

class ElfRescue extends GameBase {
  constructor(ctx) {
    super(ctx);
    this.saved = 0;
    this.energy = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.locked = false;
  }

  start() {
    this.renderShell();
    this.renderRound();
  }

  renderShell() {
    this.root.innerHTML = `
      <div class="game-shell fade-up">
        <div class="game-hud">
          <button class="icon-btn" id="g-quit" type="button">✕</button>
          <div class="grow">
            <div class="row between" style="font-size:12px;margin-bottom:4px">
              <span class="muted" id="g-round">第 1 / ${this.total} 关</span>
              <span id="g-combo" class="tag orange hidden">连击 ×1</span>
            </div>
            <div class="progress orange"><i id="g-bar" style="width:0%"></i></div>
          </div>
          <div class="pill">🧚 ${this.saved}</div>
        </div>

        <div class="game-stage" id="g-stage">
          <div class="stage-bg"></div>
          <div class="elf-cage" id="g-cage">
            <div class="cage-bars"></div>
            <div class="elf" id="g-elf">🧚</div>
          </div>
          <div class="energy-orb" id="g-orb">0%</div>
          <div class="float-msg" id="g-msg"></div>
        </div>

        <div class="game-question" id="g-q"></div>
        <div class="game-answers" id="g-a"></div>
      </div>`;

    this.root.querySelector('#g-quit').onclick = () => {
      if (confirm('退出游戏？本次成绩不保存。')) {
        this.destroy();
        this.onFinish({ aborted: true, module: 'game', correct: 0, total: 0, records: [] });
      }
    };
  }

  renderRound() {
    const q = this.current;
    if (!q) return this.finishGame();

    const stage = this.root.querySelector('#g-stage');
    const elf = this.root.querySelector('#g-elf');
    elf.textContent = ELVES[this.index % ELVES.length];
    elf.classList.remove('saved');
    stage.classList.remove('rescued');

    this.root.querySelector('#g-round').textContent = `第 ${this.index + 1} / ${this.total} 关`;
    this.root.querySelector('#g-bar').style.width = `${(this.index / this.total) * 100}%`;

    const qBox = this.root.querySelector('#g-q');
    const isListen = q.type === 'listen';
    const speakText = q.audio || q.answer?.[0] || this.current?.en || '';
    const promptText = isListen ? '🔊 听发音，选出正确内容' : String(q.prompt).replace(/\n/g, ' ');

    qBox.innerHTML = `
      <div class="q-en">${escapeHtml(promptText)}</div>
      ${q.hint ? `<div class="q-hint">${escapeHtml(q.hint)}</div>` : ''}
      ${(isListen || q.type === 'choice') ? `<button class="btn btn-sm btn-round mt-8" id="g-speak" type="button">🔊 再听一次</button>` : ''}`;
    qBox.querySelector('#g-speak')?.addEventListener('click', () => speakEn(speakText));
    if (isListen) this.later(() => speakEn(speakText), 300);

    const aBox = this.root.querySelector('#g-a');
    aBox.innerHTML = '';
    this.locked = false;

    if (q.type === 'choice' || q.type === 'judge' || q.type === 'listen') {
      const opts = q.options?.length ? q.options : ['正确', '错误'];
      shuffle(opts).forEach((o, i) => {
        const btn = el('button', { class: 'g-opt', type: 'button' }, [
          el('span', { class: 'g-key' }, String.fromCharCode(65 + i)),
          el('span', { class: 'en' }, o),
        ]);
        btn.addEventListener('click', () => this.answer(o, btn));
        aBox.appendChild(btn);
      });
    } else if (q.type === 'order') {
      // 排序题：点词块按顺序拼接
      let seq = [];
      const pool = el('div', { class: 'token-pool' });
      const built = el('div', { class: 'token-built muted' }, '点击下面的单词组成句子');
      shuffle(q.options || q.tokens || []).forEach(tok => {
        const b = el('button', { class: 'token', type: 'button', text: tok });
        b.addEventListener('click', () => {
          if (this.locked || b.disabled) return;
          seq.push(tok);
          b.disabled = true;
          built.textContent = seq.join(' ');
          built.classList.remove('muted');
        });
        pool.appendChild(b);
      });
      aBox.appendChild(built);
      aBox.appendChild(pool);
      aBox.appendChild(el('div', { class: 'row', style: 'justify-content:center;margin-top:10px' }, [
        el('button', { class: 'btn btn-ghost btn-sm', type: 'button', onclick: () => {
          seq = []; built.textContent = '点击下面的单词组成句子'; built.classList.add('muted');
          pool.querySelectorAll('button').forEach(b => { b.disabled = false; });
        } }, '重排'),
        el('button', { class: 'btn btn-primary btn-lg', type: 'button', onclick: () => this.answer(seq) }, '释放能量'),
      ]));
    } else {
      const input = el('input', {
        class: 'answer-input', type: 'text', placeholder: '输入答案后按回车',
        autocomplete: 'off', spellcheck: 'false',
      });
      input.addEventListener('keydown', e => { if (e.key === 'Enter') this.answer(input.value); });
      aBox.appendChild(input);
      aBox.appendChild(el('div', { class: 'row', style: 'justify-content:center;margin-top:10px' }, [
        el('button', { class: 'btn btn-primary btn-lg', type: 'button', onclick: () => this.answer(input.value) }, '释放能量'),
      ]));
      this.later(() => input.focus(), 60);
    }
  }

  answer(value, btn) {
    if (this.locked) return;
    if (Array.isArray(value) ? !value.length : !String(value ?? '').trim()) return;
    this.locked = true;
    const q = this.current;
    const res = this.judge(q, value);
    this.record(q, res.correct, Array.isArray(value) ? value.join(' ') : value);

    const aBox = this.root.querySelector('#g-a');
    aBox.querySelectorAll('button').forEach(b => { b.disabled = true; });
    const input = aBox.querySelector('input');
    if (input) input.disabled = true;

    if (res.correct) {
      btn?.classList.add('correct');
      this.combo += 1;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      this.energy = Math.min(100, this.energy + 100 / this.total);
      this.saved += 1;
      this.playRescue();
    } else {
      btn?.classList.add('wrong');
      this.combo = 0;
      this.playFail(res);
    }
    this.updateHud();
  }

  updateHud() {
    const orb = this.root.querySelector('#g-orb');
    orb.textContent = `${Math.round(this.energy)}%`;
    orb.style.setProperty('--p', `${this.energy}%`);
    this.root.querySelector('.pill').textContent = `🧚 ${this.saved}`;
    const combo = this.root.querySelector('#g-combo');
    if (this.combo >= 2) {
      combo.classList.remove('hidden');
      combo.textContent = `连击 ×${this.combo}`;
    } else combo.classList.add('hidden');
  }

  playRescue() {
    const stage = this.root.querySelector('#g-stage');
    const elf = this.root.querySelector('#g-elf');
    const msg = this.root.querySelector('#g-msg');
    stage.classList.add('rescued');
    elf.classList.add('saved');
    msg.textContent = ['太棒了！', '救出来啦！', '正确！', '能量 +1'][Math.floor(Math.random() * 4)];
    msg.className = 'float-msg show ok';
    this.later(() => msg.classList.remove('show'), 900);
    this.later(() => this.advance(), 1000);
  }

  playFail(res) {
    const msg = this.root.querySelector('#g-msg');
    msg.textContent = res.expected ? `正确答案：${res.expected}` : '再试一次吧';
    msg.className = 'float-msg show no';
    const stage = this.root.querySelector('#g-stage');
    stage.classList.add('shake');
    this.later(() => stage.classList.remove('shake'), 400);
    this.later(() => { msg.classList.remove('show'); this.advance(); }, 1500);
  }

  advance() {
    if (this.next()) this.renderRound();
    else this.finishGame();
  }

  finishGame() {
    this.finish({ module: 'game', saved: this.saved, maxCombo: this.maxCombo });
  }
}

registerGame(
  {
    id: 'elf-rescue',
    name: '小精灵救援行动',
    icon: '🧚',
    desc: '答对题目释放魔法能量，救出被困的小精灵。',
    supports: ['word', 'sentence', 'grammar', 'mixed'],
    difficulty: '★★☆',
  },
  ctx => new ElfRescue(ctx),
);

export default ElfRescue;
